/**
 * ImportFromTestDialog.tsx — snapshot an existing, trusted in-app test into a
 * new App Integrity fixture. Paste the test's WorkOrderDetail key (visible in
 * the test page URL); the adapter is inferred from the test type, and the
 * current inputs + computed values become the fixture and its expected values.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  fetchIntegrityAdapters,
  importFromTest,
} from '../../../services/appIntegrityService';

interface ImportFromTestDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: (message: string) => void;
  notify: (message: string, severity: 'success' | 'error' | 'info') => void;
}

const AUTO = '__auto__';

export const ImportFromTestDialog = ({ open, onClose, onImported, notify }: ImportFromTestDialogProps) => {
  const [adapters, setAdapters] = useState<Array<{ id: string; label: string }>>([]);
  const [workOrderDetailKey, setWorkOrderDetailKey] = useState('');
  const [adapterId, setAdapterId] = useState(AUTO);
  const [fixtureName, setFixtureName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      void fetchIntegrityAdapters().then(setAdapters).catch(() => setAdapters([]));
      setWorkOrderDetailKey('');
      setAdapterId(AUTO);
      setFixtureName('');
    }
  }, [open]);

  const handleImport = async () => {
    setBusy(true);
    try {
      const { fixture } = await importFromTest({
        workOrderDetailKey: workOrderDetailKey.trim(),
        adapterId: adapterId === AUTO ? undefined : adapterId,
        fixtureName: fixtureName.trim() || undefined,
      });
      onImported(`Fixture "${fixture.FixtureName}" created — review its expected values`);
    } catch (error) {
      notify(
        (error as { response?: { data?: { error?: string; message?: string } } }).response?.data
          ?.error ?? 'Import failed',
        'error',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import fixture from an existing test</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Snapshot a test whose results are verified correct. Its inputs become the replay
          payload and its current computed values become the expected answers.
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="WorkOrderDetail key"
            size="small"
            fullWidth
            placeholder="e.g. 694571FC-E8A9-4F54-B932-E23B60DF57F8"
            value={workOrderDetailKey}
            onChange={(e) => setWorkOrderDetailKey(e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace' } }}
          />
          <TextField
            label="Test type"
            size="small"
            select
            value={adapterId}
            onChange={(e) => setAdapterId(e.target.value)}
          >
            <MenuItem value={AUTO}>Detect automatically</MenuItem>
            {adapters.map((adapter) => (
              <MenuItem key={adapter.id} value={adapter.id}>
                {adapter.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Fixture name (optional)"
            size="small"
            fullWidth
            value={fixtureName}
            onChange={(e) => setFixtureName(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || workOrderDetailKey.trim().length < 30}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={handleImport}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};
